import { WinThresholds } from "ags/utils/win-magnitude";
import { Components } from "appworks/components/components";
import { PromptComponent } from "appworks/components/prompt/prompt-component";
import { PromptContentSubcomponent } from "appworks/components/prompt/prompt-content-subcomponent";
import { Layers } from "appworks/graphics/layers/layers";
import { LoaderService } from "appworks/loader/loader-service";
import { gameState } from "appworks/model/game-state";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Model } from "appworks/model/model";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { TranslationsService } from "appworks/services/translations/translations-service";
import { State } from "appworks/state-machine/states/state";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { LayerNames } from "game-layers";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { BannerComponent } from "slotworks/components/banner/banner-component";
import { AbstractMatrixComponent } from "slotworks/components/matrix/abstract-matrix-component";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { slotDefinition } from "slotworks/model/slot-definition";
import { SlotBetService } from "slotworks/services/bet/slot-bet-service";

export interface ExitFreespinsConfig {
    includeBaseWin: boolean;
    countupAmountWon: boolean;
    resetTreeToRoot: boolean;
    setToBaseReelset: boolean;
}

export class ExitFreespinsState extends State {
    protected summaryScenes: string[] = ["freespinssummary"];

    protected prompt: PromptContentSubcomponent;

    protected config: ExitFreespinsConfig = {
        includeBaseWin: true,
        countupAmountWon: true,
        resetTreeToRoot: true,
        /** @todo - make this true by default in next beta */
        setToBaseReelset: false
    };

    constructor(config?: Partial<ExitFreespinsConfig>) {
        super();

        if (config) {
            this.config = { ...this.config, ...config };
        }
    }

    public onEnter() {

        uiFlags.set(UIFlag.IDLE, false);

        this.prompt = null;
        new Sequence([
            () => this.beforePrompt(),
            () => this.setupPrompt(),
            () => this.showPrompt(),
            () => this.afterPrompt()
        ]).then(() => this.complete());
    }

    public override onExit() {
        const win = gameState.getCurrentGame().getLatestRecord().sumWins;
        gameWrapper.winningsUpdate(win);
        gameWrapper.disableFeatureMode();
        gameWrapper.setIdle();
    }

    public complete() {
        // this.setWinnings();
        super.complete();
    }

    protected beforePrompt() {
        Services.get(SoundService).event(SoundEvent.enter_basegame);

        return Contract.empty();
    }

    protected setupPrompt() {

        return Contract.wrap(() => {
            uiFlags.set(UIFlag.PROMPT, true);
            const totalWin: number = gameState.getCurrentGame().getTotalWin();
            const creditSize: number = Services.get(SlotBetService).getCreditSize();

            const totalCurrencyWin = gameiomModel.displayCurrency ? Services.get(CurrencyService).format(totalWin, true, false) : (totalWin / creditSize);
            let summaryScene: string = "freespinssummary";

            let onShowImplementation = () => {
                let message: string = "";
                const congratulationsSprite = Layers.get(LayerNames.Prompts).getSprite("winCong");
                congratulationsSprite.visible = false;
                const congratulationsText = Layers.get(LayerNames.Prompts).getText("congratulationsText");
                congratulationsText.text = Services.get(TranslationsService).get("pmt.con");
                congratulationsText.visible = false;
                const shouldCelebrate = this.isOverCelebrateThreshold(totalWin);

                if (shouldCelebrate) {
                    congratulationsSprite.visible = Model.read().settings.language === "en";
                    congratulationsText.visible = !congratulationsSprite.visible;
                    message = Services.get(TranslationsService).getFirstValid(["you_won_x"], { VALUE: totalCurrencyWin });

                } else if (totalWin !== 0 && !shouldCelebrate) {
                    message = Services.get(TranslationsService).getFirstValid(["total_win_x"], { VALUE: totalCurrencyWin });

                } else {
                    message = Services.get(TranslationsService).getFirstValid(["free_games_complete"]);
                }
                //turning on/off congratulations sprite
                this.prompt.setText("winMessage", message);
            }

            if (!this.prompt) {
                this.prompt = new PromptContentSubcomponent([summaryScene, ...this.summaryScenes], 4000, null, onShowImplementation);
            }
        });
    }

    /**
     * This method handles the logic of whether we are over the celebration threshold.
     */
    protected isOverCelebrateThreshold(featureWin: number) {
        const stakeAmount = Services.get(SlotBetService).getCreditPerLine();
        return featureWin >= 2 * stakeAmount;
    }

    protected showPrompt(): Contract<void> {
        Services.get(SoundService).customEvent("duck_freespins");
        Services.get(SoundService).customEvent("fade_freespins");
        const shouldCelebrate = this.isOverCelebrateThreshold(gameState.getCurrentGame().getTotalWin());

        if (shouldCelebrate) {
            Services.get(SoundService).customEvent("stopBigWins");
        } else {
            Services.get(SoundService).customEvent("neutralPrompt");
        }
        return Components.get(PromptComponent).showPrompt(this.prompt);
    }

    protected afterPrompt(): Contract<void> {
        return new Parallel([
            () => this.hideFreespinsComponents(),
            () => this.resetToTriggeringSpin(),
            () => this.resetMatrix()
        ]);
    }

    protected hideFreespinsComponents(): Contract<void> {
        return new Parallel([
            this.resetLayer(Layers.get("Background")),
            this.resetLayer(Layers.get("MatrixBackground")),
            this.resetLayer(Layers.get("MatrixForeground")),
            () => Contract.wrap(() => Components.get(BannerComponent)?.setMode("default"))
        ]);
    }

    protected resetLayer(layer: Layers) {
        if (!layer || layer.getCurrentScene().name === "default") {
            return () => Contract.empty();
        } else {
            return () => layer.defaultScene();
        }
    }

    protected resetToTriggeringSpin() {
        return Contract.wrap(() => {
            const gameplay = gameState.getCurrentGame();

            uiFlags.set(UIFlag.PROMPT, false);
            uiFlags.set(UIFlag.FREE_SPINS, false);

            Services.get(TransactionService).setBalance(gameState.getCurrentGame().balance);

            if (this.config.resetTreeToRoot) { gameplay.resetToRootRecord(); }

            Services.get(TransactionService).setWinnings(gameplay.getTotalWin()).execute();

            Components.get(AbstractMatrixComponent).clearStuckSymbols();

            if (this.config.setToBaseReelset) {
                this.resetReelset();
            }
        });
    }

    protected resetMatrix() {
        const gameplay = gameState.getCurrentGame();
        const record = gameplay.getRootRecord() as SpinRecord;

        if (gameplay.isRecovered) {
            return Contract.empty(); // can't recover initial reels
        }

        if (record.stops) {
            Components.get(AbstractMatrixComponent).jump(record.stops, record.reelset);
        } else {
            Components.get(AbstractMatrixComponent).jumpToGrid(record.grid);
        }

        return Contract.empty();
    }

    protected setWinnings(): void {
        const totalWin = gameState.getCurrentGame().getTotalWin();
        Services.get(TransactionService).setWinnings(totalWin, totalWin, 750).execute();
    }

    protected getTotalFreespinsWin() {
        const gameplay: Gameplay = gameState.getCurrentGame();

        let freeSpinsWin: number = 0;
        if (this.config.includeBaseWin) {
            freeSpinsWin = gameplay.getTotalWin();
        } else {
            freeSpinsWin = gameplay.getTotalFreeSpinsWin();
        }

        return freeSpinsWin;
    }

    protected resetReelset() {
        if (slotDefinition.reelsets.get("base")) {
            Components.get(AbstractMatrixComponent).setCustomReelset(slotDefinition.reelsets.get("base"));
        }
    }
}
